import { createFileRoute, Link } from "@tanstack/react-router";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";
import { LaptopVideo } from "@/components/landing/LaptopVideo";
import pharmacyImage from "@/assets/pharmacy-valuation-imac.png.asset.json";
import boomVideo from "@/assets/campaign-framework-builder.webm.asset.json";
import boomPoster from "@/assets/campaign-framework-builder-poster.jpg.asset.json";
import gatorImage from "@/assets/gatorguard-imac.png.asset.json";

const URL = "https://www.squaremigration.com/b2b-lead-magnets";
const PUBLISHED = "2026-10-06T00:00:00+00:00";
const MODIFIED = "2026-11-04T00:00:00+00:00";
const TITLE = "B2B Lead Magnets: 3 Interactive Examples That Beat PDFs";
const DESCRIPTION =
  "B2B lead magnet examples that work in 2026: a valuation calculator, a campaign assessment and a product visualizer. See what each captures for sales.";

const FAQS: FaqItem[] = [
  {
    question: "What is a lead magnet?",
    answer:
      "A lead magnet is something useful a business offers in exchange for a prospect's contact information. Classic examples are eBooks and checklists; modern B2B lead magnets are interactive tools like calculators and assessments that return a personalized answer.",
  },
  {
    question: "What is an example of a B2B lead magnet?",
    answer:
      "An ROI calculator that estimates savings from a buyer's own numbers, a readiness assessment that scores a team against a benchmark, or a product visualizer that lets a buyer preview options before requesting a quote.",
  },
  {
    question: "Are PDF lead magnets still effective?",
    answer:
      "They still collect some emails, but buyers have learned that most gated PDFs are generic. Many submit throwaway addresses, and sales gets no context about what the person actually cares about.",
  },
  {
    question: "What is the difference between a lead magnet and a lead generation tool?",
    answer:
      "A lead generation tool is an interactive lead magnet. Instead of handing over a document, it asks a few questions and gives back an answer, and those inputs tell your sales team what the lead needs.",
  },
  {
    question: "How do I choose the right lead magnet for my business?",
    answer:
      "Start with the question your buyers ask most before they talk to sales. If it involves numbers, build a calculator. If it involves readiness or maturity, build an assessment. If it involves picking options, build a visualizer.",
  },
];

const COMPARISON = [
  { classic: "eBook or whitepaper", modern: "Interactive report built from the visitor's answers" },
  { classic: "Checklist", modern: "Scored assessment with a benchmark and next steps" },
  { classic: "Pricing guide PDF", modern: "Cost or ROI calculator using the buyer's own numbers" },
  { classic: "Product catalog", modern: "Visualizer that previews options and saves a mockup" },
  { classic: "Template download", modern: "Generator that produces a tailored plan or idea list" },
];

const EXAMPLES = [
  {
    title: "Calculators",
    image: pharmacyImage.url,
    alt: "Pharmacy valuation calculator results displayed on an iMac",
    body: "Turn pricing, savings or valuation math into a tool buyers run with their own numbers. Our pharmacy valuation calculator gives owners an instant estimate before a conversation.",
    to: "/lead-generation-calculator" as const,
    cta: "See lead generation calculators",
  },
  {
    title: "Assessments",
    image: boomPoster.url,
    alt: "Boom Campaign Framework Builder assessment displayed on a laptop",
    video: boomVideo.url,
    body: "Score readiness or maturity and return a personalized result. The Boom Campaign Framework Builder and ACRE learning environment assessment both work this way.",
    to: "/assessments" as const,
    cta: "See assessments",
  },
  {
    title: "Visualizers",
    image: gatorImage.url,
    alt: "GatorGuard concrete coating visualizer displayed on an iMac",
    body: "Let buyers preview finishes, colors or configurations. GatorGuard's coating visualizer helps homeowners pick a floor and move toward a quote.",
    to: "/visualizers" as const,
    cta: "See visualizers",
  },
];

const CAPTURES = [
  "The problem the lead is trying to solve, in their own inputs",
  "Their size, budget range or current situation",
  "Their score or result, so follow-up starts with context",
  "A real email, because they want the result sent to them",
];

export const Route = createFileRoute("/b2b-lead-magnets")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          url: URL,
          datePublished: PUBLISHED,
          dateModified: MODIFIED,
          author: { "@type": "Person", name: "Chris Gould" },
          publisher: { "@id": "https://www.squaremigration.com/#organization" },
          keywords: "lead magnets, B2B lead magnets, interactive lead magnets, lead generation tools",
        }),
      },
      faqSchemaScript(FAQS),
      webPageSchemaScript({ path: "/b2b-lead-magnets", name: TITLE, description: DESCRIPTION, published: PUBLISHED, modified: MODIFIED }),
    ],
  }),
  component: LeadMagnetsPage,
});

function Section({ eyebrow, title, children }: { eyebrow: string; title: string; children: React.ReactNode }) {
  return (
    <section className="px-5 sm:px-6 py-12 md:py-16">
      <div className="mx-auto max-w-4xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{eyebrow}</p>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground">{title}</h2>
        <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed text-lg">{children}</div>
      </div>
    </section>
  );
}

function LeadMagnetsPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Guide: B2B Lead Magnets"
        title="B2B lead magnets: interactive examples that beat static PDFs."
        description="What a lead magnet is, why gated downloads stopped working for B2B, and the interactive formats that give buyers a real answer instead."
        bullets={["Definition", "Classic vs. interactive", "Real examples", "Ideas by industry", "How to choose"]}
      />

      <Section eyebrow="Definition" title="What is a lead magnet?">
        <p className="rounded-xl border border-border/60 bg-card/40 p-5 text-foreground">
          A lead magnet is something useful a business offers in exchange for a prospect's contact
          information. In B2B, the best lead magnets now answer a specific buyer question, like
          "what will this cost me?" or "how ready are we?", instead of handing over a generic document.
        </p>
        <p>
          The idea is simple: give value first, earn the conversation second. What changed is what
          buyers consider valuable.
        </p>
      </Section>

      <Section eyebrow="The Problem" title="Why gated PDFs stopped converting">
        <p>
          Buyers have downloaded enough eBooks to know most of them are recycled advice. Many now use
          throwaway email addresses, skim the first page, or skip the form entirely.
        </p>
        <p>
          Even when a PDF converts, sales receives a name and an email with no idea what the person
          actually needs. The follow-up starts cold.
        </p>
      </Section>

      <Section eyebrow="Comparison" title="Classic lead magnets vs. interactive versions">
        <div className="overflow-hidden rounded-xl border border-border/60">
          <table className="w-full text-left text-base">
            <thead className="bg-card/60 text-foreground">
              <tr>
                <th className="p-4 font-semibold">Classic</th>
                <th className="p-4 font-semibold">Interactive</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row.classic} className="border-t border-border/60">
                  <td className="p-4">{row.classic}</td>
                  <td className="p-4 text-foreground">{row.modern}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section eyebrow="Examples" title="Modern B2B lead magnet examples">
        <div className="space-y-10 sm:space-y-14">
          {EXAMPLES.map((ex, index) => (
            <article key={ex.title} className="grid items-center gap-6 border-b border-border/60 pb-10 last:border-b-0 last:pb-0 sm:grid-cols-2 sm:gap-10 sm:pb-14">
              <Link to={ex.to} aria-label={ex.alt} className={`flex aspect-[4/3] min-w-0 items-center justify-center ${index % 2 === 1 ? "sm:order-2" : ""}`}>
                {ex.video ? (
                  <LaptopVideo src={ex.video} poster={ex.image} ariaLabel={ex.alt} className="w-[88%]" />
                ) : (
                  <img src={ex.image} alt={ex.alt} loading="lazy" decoding="async" width={640} height={480} className="h-full w-full object-contain" />
                )}
              </Link>
              <div>
                <h3 className="text-2xl font-semibold text-foreground">{ex.title}</h3>
                <p className="mt-3 text-base">{ex.body}</p>
                <Link to={ex.to} className="mt-4 inline-block text-sm font-semibold text-primary hover:underline">
                  {ex.cta} →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </Section>

      <Section eyebrow="Ideas" title="B2B lead magnet ideas by industry">
        <p>Each idea below follows the same pattern as the examples above: a few questions in, a personalized answer out.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li><strong className="text-foreground">Professional services and brokers:</strong> a valuation or ROI calculator, like the pharmacy valuation tool.</li>
          <li><strong className="text-foreground">Agencies and consultants:</strong> a scored readiness or framework assessment, like the Boom campaign builder.</li>
          <li><strong className="text-foreground">Manufacturers and contractors:</strong> a product or finish visualizer, like GatorGuard's coating previewer.</li>
          <li><strong className="text-foreground">Workplace and facilities firms:</strong> an assessment of how a physical space supports the people in it.</li>
          <li><strong className="text-foreground">Software and SaaS:</strong> a cost-of-inaction or savings calculator built from the buyer's own numbers.</li>
        </ul>
      </Section>

      <Section eyebrow="Choosing" title="How to choose the right lead magnet">
        <p>Start with the question your buyers ask most before they're willing to talk to sales.</p>
        <ul className="list-disc space-y-2 pl-6">
          <li><strong className="text-foreground">It involves numbers:</strong> build a calculator.</li>
          <li><strong className="text-foreground">It involves readiness or fit:</strong> build an assessment.</li>
          <li><strong className="text-foreground">It involves picking options:</strong> build a visualizer.</li>
                  </ul>
      </Section>

      <Section eyebrow="For Sales" title="What a good lead magnet captures">
        <p>
          This is where interactive lead magnets, what we call{" "}
          <Link to="/lead-generation-tools" className="text-primary hover:underline">lead generation tools</Link>,
          pull ahead. Every answer becomes context for your team:
        </p>
        <ul className="list-disc space-y-2 pl-6">
          {CAPTURES.map((c) => <li key={c}>{c}</li>)}
        </ul>
      </Section>

      <FaqSection items={FAQS} />
      <ClosingCTA
        eyebrow="Next Step"
        headline={<>Ready to retire the <span className="text-primary">PDF?</span></>}
        subhead="Build a lead magnet buyers actually use."
        body="Meet virtually and we'll scope an interactive lead magnet for your business together."
        ctaLabel="Meet Virtually"
      />
      <RelatedServices current="/b2b-lead-magnets" />
      <Footer />
    </main>
  );
}
